import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loading } from '../components/ui/SimpleLoading';
import { Alert } from '../components/ui/SimpleAlert';
import { apiService } from '../services/api.service';

type FieldDiff = { field: string; before: unknown; after: unknown };

function defaultRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: start.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

function formatValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function actorLabel(log: any): string {
  const actor = log?.actor ?? log?.user;
  if (!actor) return '—';
  const name = [actor.firstName, actor.lastName].filter(Boolean).join(' ').trim();
  if (name && actor.email) return `${name} (${actor.email})`;
  return actor.email ?? name ?? '—';
}

/** Prefer server-provided changedFields; otherwise diff old/new client-side. */
export function resolveFieldDiffs(detail: any): FieldDiff[] {
  const payload = detail?.payload ?? {};
  const metadata = detail?.metadata ?? payload?.metadata ?? {};
  const changed = metadata?.changedFields;
  if (Array.isArray(changed) && changed.length > 0) {
    return changed.map((c: any) => ({
      field: String(c.field ?? c.path ?? ''),
      before: c.before ?? c.old ?? c.from,
      after: c.after ?? c.new ?? c.to,
    }));
  }

  const oldValues =
    (detail?.oldValues as Record<string, unknown> | undefined) ??
    (payload?.oldValues as Record<string, unknown> | undefined) ??
    {};
  const newValues =
    (detail?.newValues as Record<string, unknown> | undefined) ??
    (payload?.newValues as Record<string, unknown> | undefined) ??
    {};

  const keys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
  const diffs: FieldDiff[] = [];
  for (const field of keys) {
    const before = oldValues[field];
    const after = newValues[field];
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      diffs.push({ field, before, after });
    }
  }
  return diffs;
}

export function AuditLogs() {
  const range = defaultRange();
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [metrics, setMetrics] = useState<any | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [logRes, metricRes] = await Promise.all([
        apiService.getAuditLogs({
          entity: entity || undefined,
          action: action || undefined,
          startDate: from,
          endDate: to,
          limit: 100,
        }),
        apiService.getAuditMetrics(),
      ]);
      setLogs(logRes.logs ?? logRes.data ?? []);
      setTotal(logRes.total ?? 0);
      setMetrics(metricRes);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openDetail = async (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await apiService.getAuditLogById(id);
      setDetail(res);
    } catch (e: any) {
      setDetailError(e?.response?.data?.message ?? e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const fieldDiffs = detail ? resolveFieldDiffs(detail) : [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Auditoría</h1>
          <p className="text-sm text-gray-500 mt-1">Registro de acciones en el sistema</p>
        </div>
        <Button onClick={load}>Actualizar</Button>
      </div>

      {error && (
        <Alert variant="error" title="Error">
          {error}
        </Alert>
      )}

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total logs', value: metrics.overview?.totalLogs ?? 0 },
            { label: 'Últimas 24h', value: metrics.overview?.last24Hours ?? 0 },
            { label: 'Críticos', value: metrics.overview?.criticalEvents ?? 0 },
            { label: 'Alto riesgo', value: metrics.overview?.highRiskEvents ?? 0 },
          ].map((s) => (
            <Card key={s.label} className="text-center py-4">
              <p className="text-2xl font-semibold">{s.value}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap gap-3 mb-4">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" />
          <Input
            placeholder="Entidad (Loan, Customer…)"
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            className="max-w-xs"
          />
          <Input
            placeholder="Acción (CREATE, UPDATE…)"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="max-w-xs"
          />
          <Button variant="primary" onClick={load}>
            Filtrar
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">Sin registros en el período seleccionado.</p>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-2">{total} registro(s)</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Usuario</th>
                    <th className="px-3 py-2 text-left">Acción</th>
                    <th className="px-3 py-2 text-left">Entidad</th>
                    <th className="px-3 py-2 text-left">Operación</th>
                    <th className="px-3 py-2 text-left">Categoría</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className={`border-t cursor-pointer hover:bg-blue-50 ${
                        selectedId === log.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => openDetail(log.id)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(log.timestamp ?? log.createdAt).toLocaleString('es-AR')}
                      </td>
                      <td className="px-3 py-2">
                        {log.actor?.email ?? log.user?.email ?? '—'}
                      </td>
                      <td className="px-3 py-2">{log.action}</td>
                      <td className="px-3 py-2">
                        {log.entity}
                        <span className="text-gray-400 text-xs block">{log.entityId?.slice(0, 12)}</span>
                      </td>
                      <td className="px-3 py-2">{log.operation}</td>
                      <td className="px-3 py-2">{log.category}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {selectedId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={closeDetail} aria-hidden />
          <aside
            className="relative z-10 w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto p-6 space-y-4"
            role="dialog"
            aria-label="Detalle de auditoría"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Detalle de auditoría</h2>
                <p className="text-xs text-gray-400 mt-0.5 font-mono break-all">{selectedId}</p>
              </div>
              <Button variant="secondary" onClick={closeDetail}>
                Cerrar
              </Button>
            </div>

            {detailError && (
              <Alert variant="error" title="Error">
                {detailError}
              </Alert>
            )}

            {detailLoading ? (
              <div className="flex justify-center py-12">
                <Loading />
              </div>
            ) : detail ? (
              <>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Actor</p>
                    <p className="font-medium">{actorLabel(detail)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">IP</p>
                    <p className="font-medium">{detail.ipAddress ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">User-Agent</p>
                    <p className="font-medium break-all text-xs">{detail.userAgent ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Acción</p>
                    <p className="font-medium">{detail.action ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Entidad / ID</p>
                    <p className="font-medium">
                      {detail.entity ?? '—'}
                      {detail.entityId ? (
                        <span className="text-gray-400 text-xs block font-mono break-all">
                          {detail.entityId}
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Cambios (before / after)</h3>
                  {fieldDiffs.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin diferencias de campos registradas.</p>
                  ) : (
                    <div className="overflow-x-auto border rounded-md">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left">Campo</th>
                            <th className="px-3 py-2 text-left">Antes</th>
                            <th className="px-3 py-2 text-left">Después</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fieldDiffs.map((diff) => (
                            <tr key={diff.field} className="border-t align-top">
                              <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                                {diff.field}
                              </td>
                              <td className="px-3 py-2 text-xs break-all text-red-700/80">
                                {formatValue(diff.before)}
                              </td>
                              <td className="px-3 py-2 text-xs break-all text-green-700/80">
                                {formatValue(diff.after)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </aside>
        </div>
      )}
    </div>
  );
}
